import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from "react";
import I from "./Icons";
import { modeLabel } from "../lib/generationModes";
import { pick } from "../lib/i18n";
import { formatTokensPl } from "../lib/tokens";
import type { GenerationOutput } from "../types";

type Props = {
  outputs: GenerationOutput[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  isAdmin: boolean;
};

const copy = {
  pl: {
    preview: "Podgląd", tokens: "tokenów", download: "Pobierz", close: "Zamknij",
    previous: "Poprzednia", next: "Następna", zoomOut: "Pomniejsz", zoomIn: "Powiększ",
    reset: "Reset", hideDetails: "Ukryj szczegóły", qaDetails: "Szczegóły QA",
    promptUsed: "Prompt użyty do generacji",
  },
  en: {
    preview: "Preview", tokens: "tokens", download: "Download", close: "Close",
    previous: "Previous", next: "Next", zoomOut: "Zoom out", zoomIn: "Zoom in",
    reset: "Reset", hideDetails: "Hide details", qaDetails: "QA details",
    promptUsed: "Prompt used for generation",
  },
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

function navButtonStyle(side: "left" | "right"): CSSProperties {
  return {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    ...(side === "left" ? { left: 12 } : { right: 12 }),
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "none",
    background: "oklch(1 0 0 / .15)",
    color: "#fff",
    fontSize: 28,
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

export default function OutputLightbox({ outputs, index, onIndexChange, onClose, isAdmin }: Props) {
  const t = pick(copy);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showDetails, setShowDetails] = useState(false);
  const dragState = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= outputs.length) return;
      onIndexChange(next);
    },
    [outputs.length, onIndexChange],
  );

  useEffect(() => {
    resetView();
  }, [index, resetView]);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowRight":
          goTo(index + 1);
          break;
        case "ArrowLeft":
          goTo(index - 1);
          break;
        case "+":
        case "=":
          setZoom((value) => Math.min(MAX_ZOOM, value + ZOOM_STEP));
          break;
        case "-":
          setZoom((value) => Math.max(MIN_ZOOM, value - ZOOM_STEP));
          break;
        case "0":
          resetView();
          break;
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index, goTo, onClose, resetView]);

  const output = outputs[index];
  if (!output) return null;

  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const direction = event.deltaY < 0 ? 1 : -1;
    setZoom((value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value + direction * ZOOM_STEP)));
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLImageElement>) => {
    if (zoom <= 1) return;
    dragState.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLImageElement>) => {
    const drag = dragState.current;
    if (!drag) return;
    setOffset({ x: drag.ox + (event.clientX - drag.x), y: drag.oy + (event.clientY - drag.y) });
  };

  const handlePointerUp = () => {
    dragState.current = null;
  };

  const issues = output.qa_issues ?? [];
  const hasDetails = issues.length > 0 || (isAdmin && Boolean(output.prompt_used));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${t.preview}: ${modeLabel(output.mode)}`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "oklch(0 0 0 / .88)",
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", color: "#fff" }}
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>{modeLabel(output.mode)}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, opacity: 0.7 }}>
          {index + 1} / {outputs.length}
        </span>
        {!!output.token_usage?.total_tokens && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, opacity: 0.7 }}>
            {formatTokensPl(output.token_usage.total_tokens)} {t.tokens}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {output.image_url && (
            <a href={output.image_url} download className="btn btn-sm" onClick={(event) => event.stopPropagation()}>
              {I.download} {t.download}
            </a>
          )}
          <button ref={closeButtonRef} className="btn btn-sm btn-ghost" aria-label={t.close} onClick={onClose} style={{ color: "#fff" }}>
            {I.x}
          </button>
        </div>
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        onWheel={handleWheel}
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: zoom > 1 ? "grab" : "default",
        }}
      >
        {output.image_url && (
          <img
            src={output.image_url}
            alt={modeLabel(output.mode)}
            draggable={false}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transition: dragState.current ? "none" : "transform .12s ease",
              userSelect: "none",
            }}
          />
        )}

        {index > 0 && (
          <button aria-label={t.previous} onClick={() => goTo(index - 1)} style={navButtonStyle("left")}>
            ‹
          </button>
        )}
        {index < outputs.length - 1 && (
          <button aria-label={t.next} onClick={() => goTo(index + 1)} style={navButtonStyle("right")}>
            ›
          </button>
        )}
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", color: "#fff" }}
      >
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <button className="btn btn-sm btn-ghost" aria-label={t.zoomOut} onClick={() => setZoom((value) => Math.max(MIN_ZOOM, value - ZOOM_STEP))} style={{ color: "#fff" }}>
            −
          </button>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, minWidth: 44, textAlign: "center" }}>
            {Math.round(zoom * 100)}%
          </span>
          <button className="btn btn-sm btn-ghost" aria-label={t.zoomIn} onClick={() => setZoom((value) => Math.min(MAX_ZOOM, value + ZOOM_STEP))} style={{ color: "#fff" }}>
            +
          </button>
          {zoom > 1 && (
            <button className="btn btn-sm btn-ghost" onClick={resetView} style={{ color: "#fff" }}>
              Reset
            </button>
          )}
        </div>
        {hasDetails && (
          <button className="btn btn-sm btn-ghost" onClick={() => setShowDetails((value) => !value)} style={{ marginLeft: "auto", color: "#fff" }}>
            {showDetails ? t.hideDetails : t.qaDetails}
          </button>
        )}
      </div>

      {showDetails && hasDetails && (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            maxHeight: "32vh",
            overflow: "auto",
            padding: "12px 16px",
            background: "oklch(0 0 0 / .4)",
            color: "#fff",
            borderTop: "1px solid oklch(1 0 0 / .12)",
          }}
        >
          {issues.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {issues.map((issue, issueIndex) => (
                <li key={`${issue.type}-${issueIndex}`} style={{ fontSize: 12, display: "flex", gap: 8, alignItems: "baseline", opacity: 0.9 }}>
                  <span aria-hidden style={{ flexShrink: 0, color: issue.severity === "high" ? "#ff6b6b" : issue.severity === "medium" ? "#ffb454" : "rgba(255,255,255,.5)" }}>
                    ●
                  </span>
                  <span>{issue.description}</span>
                </li>
              ))}
            </ul>
          )}
          {isAdmin && output.prompt_used && (
            <details style={{ marginTop: issues.length > 0 ? 12 : 0 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.promptUsed}</summary>
              <pre style={{ marginTop: 8, whiteSpace: "pre-wrap", fontSize: 11, lineHeight: 1.5, opacity: 0.8, maxHeight: 200, overflow: "auto" }}>
                {output.prompt_used}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
